interface RetryOptions {
  maxRetries?: number;
  retryDelay?: number;
  timeout?: number;
  onRetry?: (attempt: number, error: any) => void;
  shouldRetry?: (error: any) => boolean;
}

export class ApiError extends Error {
  public status: number;
  public data?: any;

  constructor(status: number, message: string, data?: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

const isLocalHostName = (host: string | undefined): boolean =>
  host === 'localhost' || host === '127.0.0.1';

let isDev = false;
try {
  // @ts-ignore
  if (import.meta.env?.DEV || import.meta.env?.MODE === 'test') {
    isDev = true;
  }
} catch {
  // Ignore errors accessing import.meta
}

// Fallback for Node.js/Playwright environment where import.meta.env might be undefined
if (!isDev && typeof process !== 'undefined') {
  isDev = process.env.NODE_ENV === 'test' || process.env.NODE_ENV === 'development';
}

const isDevBuild = isDev;

export class ApiClient {
  private static csrfToken: string | null = null;
  private static csrfTokenPromise: Promise<string> | null = null;
  private static get isLocalDev(): boolean {
    if (typeof window === 'undefined') {
      return isDevBuild;
    }
    return isDevBuild && isLocalHostName(window.location.hostname);
  }

  private static isNetworkError(error: any): boolean {
    return error.name === 'AbortError' ||
      error.name === 'TypeError' ||
      error.message === 'Failed to fetch' ||
      error.message === 'Network request failed';
  }

  private static async getCsrfToken(): Promise<string> {
    // In development/test environments, don't use CSRF tokens
    // Check if we're in development mode based on the URL
    if (this.isLocalDev) {
      return 'test-token';
    }

    // If we already have a promise in progress, wait for it
    if (this.csrfTokenPromise) {
      return this.csrfTokenPromise;
    }

    // If we have a cached token, return it
    if (this.csrfToken) {
      return this.csrfToken;
    }

    // Fetch new token
    this.csrfTokenPromise = fetch('/api/selfservice/csrf-token', {
      method: 'GET',
      credentials: 'include'
    })
      .then(response => response.json())
      .then(data => {
        this.csrfToken = data.token;
        this.csrfTokenPromise = null;
        return data.token;
      })
      .catch(error => {
        this.csrfTokenPromise = null;
        throw error;
      });

    return this.csrfTokenPromise;
  }

  private static async delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  static async fetchWithRetry(
    url: string,
    options: RequestInit = {},
    retryOptions: RetryOptions = {}
  ): Promise<Response> {
    const {
      maxRetries = 3,
      retryDelay = 1000,
      timeout = 10000,
      onRetry,
      shouldRetry = (error) => this.isNetworkError(error)
    } = retryOptions;

    let lastError: any;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        // Get headers from options
        const headers: Record<string, string> = {
          ...(options.headers as Record<string, string> || {})
        };

        // CSRF-beskyttelse: Aktivér i ikke-udviklingsmiljøer
        if (!this.isLocalDev && options.method && ['POST', 'PUT', 'DELETE'].includes(options.method)) {
          const csrfToken = await this.getCsrfToken();
          headers['X-CSRF-TOKEN'] = csrfToken;
        }

        // Create abort controller for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        const response = await fetch(url, {
          ...options,
          headers,
          credentials: !this.isLocalDev ? 'include' : undefined,
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        // Don't retry on successful responses or client errors (4xx)
        if (response.ok) {
          return response;
        }

        if (response.status >= 400 && response.status < 500) {
          // Client error - try to parse error message
          let errorMessage = `Client error: ${response.status}`;
          try {
            const errorData = await response.json();
            if (errorData.error) errorMessage = errorData.error;
          } catch { /* ignore */ }
          throw new ApiError(response.status, errorMessage);
        }

        // Server error (5xx) - throw to trigger retry
        throw new ApiError(response.status, `Server error: ${response.status}`);

      } catch (error) {
        lastError = error;

        // Check if we should retry
        if (attempt < maxRetries && shouldRetry(error)) {
          if (onRetry) {
            onRetry(attempt + 1, error);
          }

          // Exponential backoff: delay * 2^attempt
          const delayMs = retryDelay * Math.pow(2, attempt);
          await this.delay(delayMs);
          continue;
        }

        // No more retries or error is not retryable
        break;
      }
    }

    throw lastError;
  }

  static async postJSON(url: string, data: any, retryOptions?: RetryOptions): Promise<Response> {
    return this.fetchWithRetry(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    }, retryOptions);
  }

  static async getJSON(url: string, retryOptions?: RetryOptions): Promise<Response> {
    return this.fetchWithRetry(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    }, retryOptions);
  }

  static clearCsrfToken(): void {
    this.csrfToken = null;
    this.csrfTokenPromise = null;
  }
}
